/**
 * The respondent's view of a survey — ONE implementation shared by the public
 * answer page (`/s/:token`, `PublicSurvey.tsx`) and the staff preview
 * (`SurveyPreviewDialog`), so a preview shows exactly what a respondent sees.
 *
 * - `SurveyRespondentShell` — the page frame: the shared `PublicPageShell`
 *   (components/ui/public-page-shell.tsx) with the survey's safety line.
 *   The public page also renders its loading / error / unavailable states in it.
 * - `SurveyRespondentView` — the card content: the `SurveyBrandHeader` (the
 *   org logo when the survey has a snapshot, and the line above the title —
 *   `surveyIntroLine`: the survey's own line, else "{Project} · {Org}", else
 *   "A survey from {Org}"), title, description, required note, the shared
 *   `SurveyForm` in the survey's layout (`displayMode`), and the thank-you
 *   state (the logo again, the survey's own thank-you message or the default
 *   sentence, and an optional website button in the primary colour).
 *
 * The view owns the thank-you state (`initiallySubmitted` opens on it): `onSubmit` resolves `true` when the
 * response was accepted (thank-you), anything else keeps the form (the host
 * shows its own error through `formChildren`).
 *
 * SECURITY — PLAIN TEXT ONLY: every survey string is a React text node (no
 * markup, no links, no style from data); the questions go through `SurveyForm`.
 * The two non-text inputs are server-built: `logoUrl` (the public bucket URL of
 * the API's sniffed PNG / JPEG / WebP copy) and `websiteButton` (built by the
 * shared `surveyWebsiteButton`: an absolute http(s) URL from the one
 * normaliser, opened in a new tab with `noopener noreferrer nofollow`; its
 * words — the editor's text, else "Visit {hostname}" / "Visit {org}" — are a
 * text node via `surveyWebsiteButtonText`).
 */
import { useCallback, useState, type ReactNode } from 'react';
import type { SurveyAnswers, SurveyDisplayMode, SurveyQuestion, SurveyWebsiteButton } from '@releve/shared';
import { Button } from '@/components/ui/button';
import { PublicPageShell, type PublicPageShellProps } from '@/components/ui/public-page-shell';
import { useTranslation } from '@/hooks/useTranslation';
import { surveyIntroLine, surveyWebsiteButtonText } from '../surveyLabels';
import { SurveyBrandHeader } from './SurveyBrandHeader';
import { SurveyForm, type SurveyFormProps } from './SurveyForm';

export type SurveyRespondentShellProps = Omit<PublicPageShellProps, 'safetyNote'>;

/** The shared `PublicPageShell` with the survey's own safety line ("…in a survey."). */
export function SurveyRespondentShell(props: Readonly<SurveyRespondentShellProps>) {
  const { t } = useTranslation('surveys');
  return <PublicPageShell {...props} safetyNote={t('public.safety')} />;
}

export interface SurveyRespondentViewProps {
  orgName: string;
  title: string;
  description?: string | null;
  questions: readonly SurveyQuestion[];
  /** Resolve `true` when the response was accepted — the view then thanks the respondent. */
  onSubmit: (answers: SurveyAnswers) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
  /** Open on the thank-you state (the public page: this browser already responded). */
  initiallySubmitted?: boolean;
  /** Rendered inside the form, after the questions (honeypot, submit error). */
  formChildren?: ReactNode;
  /**
   * A host note (the preview says nothing was saved): replaces the DEFAULT
   * thank-you sentence, or — when the survey has its own message — follows it
   * as a second muted line.
   */
  thankYouBody?: string;
  /** The survey's own thank-you text — replaces the default sentence; shown before `thankYouBody`. */
  thankYouMessage?: string | null;
  /** The survey's logo snapshot (public URL), shown above the eyebrow and on the thank-you screen. */
  logoUrl?: string | null;
  /** The survey's own line above the title; NULL → the default (`surveyIntroLine`). */
  introLine?: string | null;
  /** The survey's project name — feeds the default line ("{Project} · {Org}"). */
  projectName?: string | null;
  /** The layout: every question on one page (default) or one question at a time. */
  displayMode?: SurveyDisplayMode;
  /** The thank-you screen's website button (`surveyWebsiteButton`); NULL → none. */
  websiteButton?: SurveyWebsiteButton | null;
  /** Respondent analytics (the public page only) — passed straight to `SurveyForm`. */
  onQuestionAnswered?: SurveyFormProps['onQuestionAnswered'];
  onStepReached?: SurveyFormProps['onStepReached'];
  onSubmitBlocked?: SurveyFormProps['onSubmitBlocked'];
}

/**
 * "Questions marked * are required." with its `*` in the legends' red. The
 * star stays in the accessibility tree (unlike the legends' decorative star,
 * which has an sr-only word beside it): here it is part of the sentence.
 */
function RequiredNote({ text }: Readonly<{ text: string }>) {
  const at = text.indexOf('*');
  return (
    <p className="text-xs text-muted-foreground">
      {at < 0 ? (
        text
      ) : (
        <>
          {text.slice(0, at)}
          <span className="text-destructive">*</span>
          {text.slice(at + 1)}
        </>
      )}
    </p>
  );
}

export function SurveyRespondentView({
  orgName,
  title,
  description,
  questions,
  onSubmit,
  isSubmitting = false,
  initiallySubmitted = false,
  formChildren,
  thankYouBody,
  thankYouMessage,
  logoUrl,
  introLine,
  projectName,
  displayMode = 'all',
  websiteButton,
  onQuestionAnswered,
  onStepReached,
  onSubmitBlocked,
}: SurveyRespondentViewProps) {
  const { t } = useTranslation('surveys');
  const [submitted, setSubmitted] = useState(initiallySubmitted);

  const handleSubmit = useCallback(
    async (answers: SurveyAnswers) => {
      if ((await onSubmit(answers)) === true) setSubmitted(true);
    },
    [onSubmit],
  );

  if (submitted) {
    return (
      <div className="space-y-4 text-center" role="status">
        <SurveyBrandHeader orgName={orgName} logoUrl={logoUrl} centered decorativeLogo />
        <h1 className="text-2xl font-semibold">{t('public.thankYouTitle')}</h1>
        <p className="whitespace-pre-line text-sm text-muted-foreground">
          {thankYouMessage || thankYouBody || t('public.thankYouBody')}
        </p>
        {/* A host note (the preview's "nothing was saved") must survive a custom message. */}
        {thankYouMessage && thankYouBody && <p className="text-xs text-muted-foreground">{thankYouBody}</p>}
        {websiteButton && (
          <div className="flex justify-center">
            <Button asChild className="h-auto min-h-10 max-w-full whitespace-normal break-all">
              <a href={websiteButton.href} target="_blank" rel="noopener noreferrer nofollow">
                {surveyWebsiteButtonText(websiteButton, t)}
              </a>
            </Button>
          </div>
        )}
      </div>
    );
  }

  const hasRequired = questions.some((q) => q.required);
  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <SurveyBrandHeader
          orgName={orgName}
          logoUrl={logoUrl}
          line={surveyIntroLine({ introLine, projectName, orgName }, t)}
        />
        <h1 className="text-2xl font-semibold">{title}</h1>
        {description && <p className="whitespace-pre-line text-sm text-muted-foreground">{description}</p>}
        {hasRequired && <RequiredNote text={t('public.requiredNote')} />}
      </header>

      <SurveyForm
        questions={questions}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel={t('public.submit')}
        submittingLabel={t('public.submitting')}
        displayMode={displayMode}
        onQuestionAnswered={onQuestionAnswered}
        onStepReached={onStepReached}
        onSubmitBlocked={onSubmitBlocked}
      >
        {formChildren}
      </SurveyForm>
    </div>
  );
}
